
import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UserRole from './UserRole';
import Statusform from './Statusform';
import StepModify from './otherComponent/StepModify';
import { IDOCUMENT } from 'types/auth';

type StatusItem = {
  _id: string;
  name: string;
  uid: string;
};

type DocumentFormProps = {
  selectedNodeData: IDOCUMENT;
  selectNode: any;
  onSubmit: (data: IDOCUMENT) => void;
  onClose: () => void;
};

const DocumentForm = ({ selectedNodeData, onSubmit, onClose, selectNode }: DocumentFormProps) => {
  const [initialValues, setInitialValues] = useState<IDOCUMENT | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);  

  useEffect(() => {
    if (!selectedNodeData) return;

    const { name, title, description, settings, component = {}, componentType, data, onComplete } = selectedNodeData;
    const actor = selectNode?.data?.step?.actor?.map((a: any) => a.uid) ?? [];
  

    setInitialValues({
      name,
      title,
      description,
      settings: {
        maxSize: settings?.maxSize ?? 10,
        watermark: settings?.watermark ?? false,
        coordinate: settings?.coordinate ?? false
      },
      component,
      componentType,
      data: {
        title: data?.label ?? name ?? 'Document',
        step: { ...(data?.step || {}), actor, componentType }
      },
      actor,
      stepType: selectNode?.data?.step?.stepType || 'DOCUMENT',
      onCompleteStatusUid:selectNode?.data?.step?.onComplete?.status?.uid || '',
      onComplete
    });
  }, [selectedNodeData, selectNode]);

  return initialValues ? (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => {
        const selectedStatus = statuses.find((s) => s.uid === values.onCompleteStatusUid);

        const payload: IDOCUMENT = {
          ...values,
          actor: values.actor,
          component: { ...values.component, name: values.name },
          data: {
            ...values.data,
            step: {
              ...values.data.step,
              actor: values.actor,
              componentType: values.componentType
            }
          },
          onComplete: selectedStatus ? { status: selectedStatus } : {}
        };

        console.log('Submitting payload:', payload);
        onSubmit(payload);
      }}
    >
      {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, submitForm }) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Paper elevation={0} sx={{ p: 4, width: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h4" fontWeight="bold">
                {values.stepType || 'Document'}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {isEditingTitle ? (
                  <>
                    <TextField
                      name="data.title"
                      value={values.data.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      size="small"
                      autoFocus
                    />
                    <Button variant="contained" size="small" onClick={() => setIsEditingTitle(false)}>
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outlined" size="small" onClick={() => setIsEditingTitle(true)}>
                      Edit
                    </Button>
                    {selectNode && (
                      <StepModify
                        selectedNode={selectNode}
                        onStepTypeChange={(newType) => {
                          const type = newType.toUpperCase();
                          setFieldValue('stepType', type);
                          setFieldValue('componentType', type);
                          setFieldValue('data.title', type);
                          setTimeout(() => submitForm(), 0);
                        }}
                      />
                    )}
                  </>
                )}
              </Box>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={4}><Typography>Title:</Typography></Grid>
                <Grid item xs={8}>
                  <TextField
                    size="small"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={4}><Typography>Description:</Typography></Grid>
                <Grid item xs={8}>
                  <TextField
                    size="small"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    multiline
                    minRows={3}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" ml={2} my={2}>Settings</Typography>
                  <Accordion sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography fontWeight="bold">Fields Configuration</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={4}><Typography>Max Size (MB):</Typography></Grid>
                        <Grid item xs={8}>
                          <TextField
                            size="small"
                            type="number"
                            name="settings.maxSize"
                            value={values.settings.maxSize}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={4}><Typography>Watermark:</Typography></Grid>
                        <Grid item xs={8}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="settings.watermark"
                                checked={values.settings.watermark}
                                onChange={handleChange}
                              />
                            }
                            label="Enable"
                          />
                        </Grid>
                        <Grid item xs={4}><Typography>Coordinate:</Typography></Grid>
                        <Grid item xs={8}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="settings.coordinate"
                                checked={values.settings.coordinate}
                                onChange={handleChange}
                              />
                            }
                            label="Enable"
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>

              <UserRole name="actor" />

                        <Statusform
                name="onCompleteStatusUid"
                 selectedNode={selectNode}
                onStatusesLoaded={(list) => setStatuses(list)}
              />

              <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <Button color="error" onClick={onClose}>Cancel</Button>
                <Button variant="contained" color="primary" type="submit">Save</Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Formik>
  ) : null;
};

export default DocumentForm;
